import { useEffect, useLayoutEffect, useRef } from 'react';
import type { RefObject } from 'react';
import type { ILayoutItemFilter, IUserFilterConfig } from '@dbl/core/dashboard';

import {
  applyTopFiltersCollapseToFirstRow,
  restoreTopFiltersChildrenDisplay,
} from '@/pages/datalab/components/DashboardLayout/components/TopFilters/utils/collapseToFirstRow';

interface IUseTopFiltersCollapseEffectsParams {
  wrapRef: RefObject<HTMLDivElement>;
  enableCollapseFilter: boolean;
  isEdit: boolean;
  showToggleButton: boolean;
  isCollapsed: boolean;
  setIsCollapsed: (next: boolean | ((prev: boolean) => boolean)) => void;
  generalFilters: ILayoutItemFilter[];
  userFilter?: IUserFilterConfig;
}

interface IUseTopFiltersCollapseEffectsResult {
  markPendingAutoExpand: () => void;
}

/**
 * TopFilters 的折叠相关副作用抽离：
 * - 编辑态新增筛选器后：若出现多行则自动展开一次
 * - 折叠态：按“第一行能完整放下”的规则隐藏多余子元素
 * - 监听容器尺寸 / 子节点增删：触发折叠重算，覆盖“删除第一行出现空位但宽度不变”的场景
 */
export const useTopFiltersCollapseEffects = (
  params: IUseTopFiltersCollapseEffectsParams,
): IUseTopFiltersCollapseEffectsResult => {
  const {
    wrapRef,
    enableCollapseFilter,
    isEdit,
    showToggleButton,
    isCollapsed,
    setIsCollapsed,
    generalFilters,
    userFilter,
  } = params;

  const pendingAutoExpandRef = useRef<boolean>(false);
  const collapsePrevWidthRef = useRef<number | null>(null);

  const markPendingAutoExpand = (): void => {
    pendingAutoExpandRef.current = true;
  };

  useLayoutEffect(() => {
    if (!enableCollapseFilter || !isEdit) {
      pendingAutoExpandRef.current = false;
      return;
    }

    if (!pendingAutoExpandRef.current) {
      return;
    }

    // 新增后只有在“确实出现多行”时才自动展开，避免单行场景也触发 UI 变化。
    if (showToggleButton) {
      setIsCollapsed(false);
    }

    // 无论是否展开，都只消费一次新增标记，避免后续 resize/locale 变化误触发展开。
    pendingAutoExpandRef.current = false;
  }, [
    enableCollapseFilter,
    generalFilters,
    isEdit,
    setIsCollapsed,
    showToggleButton,
    userFilter,
  ]);

  useLayoutEffect(() => {
    if (!enableCollapseFilter || !isCollapsed || !showToggleButton) {
      // 展开态或不需要折叠按钮时，恢复所有子元素显示
      const root = wrapRef.current;
      if (!root) return;
      restoreTopFiltersChildrenDisplay(root);
      return;
    }

    const root = wrapRef.current;
    if (!root) return;

    const rafId = window.requestAnimationFrame(() => {
      applyTopFiltersCollapseToFirstRow(root);
    });

    return () => {
      window.cancelAnimationFrame(rafId);
      restoreTopFiltersChildrenDisplay(root);
    };
  }, [enableCollapseFilter, isCollapsed, showToggleButton, wrapRef]);

  useEffect(() => {
    if (!enableCollapseFilter || !isCollapsed || !showToggleButton) {
      return;
    }

    const root = wrapRef.current;
    if (!root) return;

    if (collapsePrevWidthRef.current === null) {
      collapsePrevWidthRef.current = Math.round(root.getBoundingClientRect().width);
    }
    let rafId: number | null = null;
    const scheduleApply = (): void => {
      if (rafId !== null) return;
      rafId = window.requestAnimationFrame(() => {
        rafId = null;
        applyTopFiltersCollapseToFirstRow(root);
      });
    };

    let ro: ResizeObserver | null = null;
    let mo: MutationObserver | null = null;
    if (typeof ResizeObserver === 'undefined') {
      window.addEventListener('resize', scheduleApply);
    } else {
      ro = new ResizeObserver(() => {
        const nextWidth = Math.round(root.getBoundingClientRect().width);
        if (nextWidth === collapsePrevWidthRef.current) {
          return;
        }
        collapsePrevWidthRef.current = nextWidth;
        scheduleApply();
      });
      ro.observe(root);
    }

    /**
     * 覆盖“编辑态折叠 + 删除第一行筛选器”的场景：
     * - 删除会让第一行出现空位，但 root 宽度可能不变，ResizeObserver 不触发；
     * - 若不重算折叠，隐藏项仍是 display:none，无法补位显示。
     *
     * 这里只监听 childList（节点增删），不监听 attributes，避免与折叠逻辑写入 display 形成反馈回路。
     */
    if (typeof MutationObserver !== 'undefined') {
      mo = new MutationObserver(() => {
        scheduleApply();
      });
      mo.observe(root, { childList: true, subtree: true });
    }

    return () => {
      if (rafId !== null) {
        window.cancelAnimationFrame(rafId);
        rafId = null;
      }
      if (typeof ResizeObserver === 'undefined') {
        window.removeEventListener('resize', scheduleApply);
      } else {
        ro?.disconnect();
      }
      mo?.disconnect();
    };
  }, [enableCollapseFilter, isCollapsed, showToggleButton, wrapRef]);

  return { markPendingAutoExpand };
};

